/**
 * Compares two semantic versions and determines if terms acceptance is required
 *
 * Only triggers for major version changes (e.g., 1.1 -> 2.0)
 * Minor version changes don't require re-acceptance (e.g., 1.1 -> 1.2)
 */
export function isTermsAcceptanceRequired(
	currentVersion: string | null | undefined,
	latestVersion: string | null | undefined,
): boolean {
	// If user has never accepted terms
	if (!currentVersion) return true;

	// If we can't determine the latest version
	if (!latestVersion) return false;

	try {
		const current = parseSemanticVersion(currentVersion);
		const latest = parseSemanticVersion(latestVersion);

		// Compare major versions only
		return current.major !== latest.major;
	} catch (error) {
		console.warn('Error parsing semantic versions:', error);
		// On parsing error, require acceptance to be safe
		return true;
	}
}

/**
 * Parses a semantic version string into components
 * Handles parsing issues gracefully with validation
 */
function parseSemanticVersion(version: string): { major: number; minor: number } {
	if (!version || typeof version !== 'string') {
		throw new Error('Invalid version string');
	}

	const cleanVersion = version.trim().replace(/^v/, ''); // Remove 'v' prefix if present
	const parts = cleanVersion.split('.');

	if (parts.length !== 2) {
		throw new Error(`Invalid semantic version format: ${version}`);
	}

	const [majorStr, minorStr] = parts;
	const major = parseInt(majorStr, 10);
	const minor = parseInt(minorStr, 10);

	if (isNaN(major) || isNaN(minor)) {
		throw new Error(`Non-numeric version components: ${version}`);
	}

	if (major < 0 || minor < 0) {
		throw new Error(`Negative version components not allowed: ${version}`);
	}

	return { major, minor };
}

export { parseSemanticVersion };
